import {pick} from '@benchling/js-utils';

import {PILL_DISABLED_DUE_TO_DOWNSTREAM_FLAG_ROWS} from 'analysis-tool/AnalysisToolConstants';
import {GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import getDownstreamSteps from 'analysis-tool/data/getDownstreamSteps';
import {VIEW_END_STEP_TYPES} from 'analysis-tool/data/LineageStepTypes';
import {checkCanInsertStep} from 'analysis-tool/EditStepHelpers';
import isAnalysisReadOnly from 'analysis-tool/isAnalysisReadOnly';
import {
  AnalysisRightSidebarItem,
  AnalysisRightSidebarItemType,
} from 'analysis-tool/right-sidebar/RightSidebarTypes';
import {DataFrameSpec} from 'data-frames/DataFrameTypes';
import {ColumnHeaderTransformOptions} from 'data-frames/header-menu/DataFrameHeaderMenuOptions';
import {DataFrameReadOnlyInfo} from 'data-frames/header-menu/DataFrameReadOnlyInfo';
import {
  PipelineStepDataKind,
  PipelineStepStatusCode,
  PipelineStepType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  PipelineGraphQL,
  pipelineStepIsStepType,
  PipelineStep,
  PipelineStepData,
  PipelineViewGraphQL,
} from 'pipelines/PipelineTypes';
import {getOutputDataForView} from 'pipelines/PipelineViewUtils';
import {
  getOutputStepDataRawDatas,
  getPipelineStepRawDatasOfKind,
  pipelineStepRawDataIsOfKind,
} from 'pipelines/StepDataUtils';
import {getTableLineageAncestorSteps} from 'pipelines/StepLineageUtils';

export function getAncestorStepsByType(
  analysis: PipelineGraphQL,
  step: GetAnalysisPipelineStep,
  stepType: PipelineStepType
): Array<GetAnalysisPipelineStep> {
  return getTableLineageAncestorSteps(analysis, step).filter((step) => step.definition.stepType === stepType);
}

export interface RightSidebarItemState {
  onChangeSelectedRightSidebarItem: (item: AnalysisRightSidebarItem | null) => void;
  selectedRightSidebarItem: AnalysisRightSidebarItem | null;
}

export function updateRightSidebarItemOnAddingStep(
  newStep: GetAnalysisPipelineStep,
  rightSidebarItemState: RightSidebarItemState
): void {
  if (rightSidebarItemState.selectedRightSidebarItem == null) {
    return;
  }

  if (rightSidebarItemState.selectedRightSidebarItem.type === AnalysisRightSidebarItemType.EDIT_VIEW) {
    rightSidebarItemState.onChangeSelectedRightSidebarItem({
      type: AnalysisRightSidebarItemType.EDIT_VIEW,
      viewReference: {
        stepId: newStep.id,
        outputReference: {
          stepDataName: newStep.outputStepDatas[0].name,
          rawDataIndex: 0,
        },
      },
    });
  }
}

export function setRightSidebarItemIsUpdating(rightSidebarItemState: RightSidebarItemState): void {
  if (rightSidebarItemState.selectedRightSidebarItem?.type === AnalysisRightSidebarItemType.EDIT_VIEW) {
    rightSidebarItemState.onChangeSelectedRightSidebarItem({
      ...rightSidebarItemState.selectedRightSidebarItem,
      isUpdating: true,
    });
  }
}

/**
 * Get the existing transform step to modify, if any.
 *
 * If the last step of a view – excluding any TABLE_VIEW, CHART_VIEW or REGRESSION_VIEW steps – is that step
 * type step, then new filters should be added onto that FILTER_DATASET step. Otherwise, a new step should be
 * added.
 *
 * @package
 */
export function getExistingHeaderMenuTransformStep<StepType extends keyof ColumnHeaderTransformOptions>(
  view: PipelineViewGraphQL,
  stepType: StepType
): PipelineStep<StepType, 0> | null {
  for (const lineageNode of view.lineage.toReversed()) {
    if (pipelineStepIsStepType(stepType, 0, lineageNode.step)) {
      return lineageNode.step;
    }
    if (VIEW_END_STEP_TYPES.includes(lineageNode.step.stepType)) {
      continue;
    }
    return null;
  }
  return null;
}

/**
 * Get the data frame spec on which a new transform would be applied.
 *
 * @package
 */
export function getCurrentDataFrameSpec(
  view: PipelineViewGraphQL,
  stepType: keyof ColumnHeaderTransformOptions
): DataFrameSpec {
  const emptySpec = {columns: [], name: 'Unknown'};

  // If there is an existing filter step, the current data frame spec is the input to the filter step.
  const existingTransformStep = getExistingHeaderMenuTransformStep(view, stepType);
  if (existingTransformStep) {
    const dataFrames = getOutputStepDataRawDatas(existingTransformStep, 'Output dataset');
    return dataFrames.length === 1 ? pick(dataFrames[0], 'name', 'columns') : emptySpec;
  }

  // Otherwise, the current data frame spec comes from whichever step data to which a new transform step would
  // be connected.
  const stepDataInfo = getStepDataToConnectNewTransformStep(view);
  if (stepDataInfo) {
    const dataFrames = getPipelineStepRawDatasOfKind(PipelineStepDataKind.DATASET, stepDataInfo.stepData);
    return dataFrames.length > (stepDataInfo.stepDataIndex ?? 0)
      ? pick(dataFrames[stepDataInfo.stepDataIndex ?? 0], 'name', 'columns')
      : emptySpec;
  }

  return emptySpec;
}

/**
 * Get the step data to connect a new transform step to.
 *
 * This is either the final output step data of the view or – if the final step is a TABLE_VIEW, CHART_VIEW or
 * REGRESSION step – the input step data of the final step.
 *
 * @package
 */
export function getStepDataToConnectNewTransformStep(view: PipelineViewGraphQL): {
  stepData: PipelineStepData;
  stepDataIndex: number | null;
} | null {
  const {step, stepOutputReference} = view.lineage[view.lineage.length - 1];

  if (VIEW_END_STEP_TYPES.includes(step.stepType)) {
    return {stepData: step.inputStepDatas[0], stepDataIndex: 0};
  } else if (stepOutputReference != null) {
    return {stepData: stepOutputReference.outputStepData, stepDataIndex: stepOutputReference.rawDataIndex};
  }
  return null;
}

/**
 * Check if header menu options should be enabled.
 * @package
 */
export function getReadOnlyInfo(analysis: PipelineGraphQL, view: PipelineViewGraphQL): DataFrameReadOnlyInfo {
  if (isAnalysisReadOnly(analysis)) {
    return {isReadOnly: true};
  }

  // Views should not be empty; if it is, disable transform options
  if (view.lineage.length === 0) {
    return {isReadOnly: true};
  }

  const terminalStep = view.lineage[view.lineage.length - 1].step;

  // Disable edits while the step is running
  if (terminalStep.statusCode !== PipelineStepStatusCode.SUCCEEDED) {
    return {isReadOnly: true};
  }

  // Disable edits if data isn't available
  const outputRawData = getOutputDataForView(view);
  if (outputRawData == null || !pipelineStepRawDataIsOfKind(PipelineStepDataKind.DATASET, outputRawData)) {
    return {isReadOnly: true};
  }

  if (
    [...getDownstreamSteps(analysis, terminalStep.id)].some(
      (step) => step.definition.stepType === PipelineStepType.FLAG_ROWS
    )
  ) {
    return {
      isReadOnly: true,
      allUpdatesDisabledMessage: PILL_DISABLED_DUE_TO_DOWNSTREAM_FLAG_ROWS,
    };
  }

  const {canEdit, message} = checkCanInsertStep(analysis, terminalStep.id, 'view', outputRawData.id);

  return canEdit ? {isReadOnly: false} : {isReadOnly: false, allUpdatesDisabledMessage: message};
}
